import { useState, useEffect, useCallback } from 'react';
import {
  MessageSquare,
  Loader2,
  RefreshCw,
  AlertCircle,
  AlertTriangle,
  X,
  CheckCircle2,
  Trash2,
  AlertOctagon,
  Ban,
} from 'lucide-react';
import { FlaggedMessageCard, MessageContextViewer } from '../../components/Chat';

// Types
interface FlaggedMessage {
  id: number;
  squadId: number;
  squadName: string;
  senderId: number;
  senderName: string;
  senderEmail: string;
  senderAvatarUrl?: string;
  messageText: string;
  messageType: string;
  attachmentUrl?: string;
  aiFlagged: boolean;
  aiToxicityScore?: number;
  aiFlagReason?: string;
  severity: 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';
  reviewed: boolean;
  createdAt: string;
}

interface FlaggedStats {
  total: number;
  critical: number;
  high: number;
  medium: number;
  low: number;
}

interface PageResponse {
  content: FlaggedMessage[];
  totalElements: number;
  totalPages: number;
  number: number;
  size: number;
}

// API helper
async function fetchApi<T>(url: string, options: RequestInit = {}): Promise<T> {
  const response = await fetch(url, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const error = await response.text();
    throw new Error(error || `HTTP ${response.status}`);
  }

  return response.json();
}

type Severity = 'ALL' | 'LOW' | 'MEDIUM' | 'HIGH' | 'CRITICAL';

export function FlaggedMessagesPage() {
  // State
  const [messages, setMessages] = useState<FlaggedMessage[]>([]);
  const [stats, setStats] = useState<FlaggedStats | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Filters
  const [severityFilter, setSeverityFilter] = useState<Severity>('ALL');

  // Pagination
  const [page, setPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  // Selected message and context
  const [selectedMessage, setSelectedMessage] = useState<FlaggedMessage | null>(null);
  const [contextMessages, setContextMessages] = useState<FlaggedMessage[]>([]);
  const [isLoadingContext, setIsLoadingContext] = useState(false);
  const [isActioning, setIsActioning] = useState(false);

  // Load messages
  const loadMessages = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const severityParam = severityFilter !== 'ALL' ? `&severity=${severityFilter}` : '';
      const url = `/api/v1/admin/chat/flagged?page=${page}&size=20${severityParam}`;

      const data = await fetchApi<PageResponse>(url);
      setMessages(data.content);
      setTotalPages(data.totalPages);
      setTotalItems(data.totalElements);
    } catch (err) {
      console.error('Failed to load flagged messages:', err);
      // Use mock data for development
      setMessages(getMockMessages());
      setTotalPages(1);
      setTotalItems(5);
    } finally {
      setIsLoading(false);
    }
  }, [severityFilter, page]);

  // Load stats
  const loadStats = useCallback(async () => {
    try {
      const data = await fetchApi<FlaggedStats>('/api/v1/admin/chat/stats');
      setStats(data);
    } catch (err) {
      console.error('Failed to load stats:', err);
      // Use mock data for development
      setStats({
        total: 23,
        critical: 3,
        high: 7,
        medium: 8,
        low: 5,
      });
    }
  }, []);

  // Load context for selected message
  const loadContext = useCallback(async (messageId: number) => {
    setIsLoadingContext(true);
    try {
      const data = await fetchApi<FlaggedMessage[]>(
        `/api/v1/admin/chat/flagged/${messageId}/context?count=5`
      );
      setContextMessages(data);
    } catch (err) {
      console.error('Failed to load context:', err);
      // Use mock context
      const mockMsg = messages.find(m => m.id === messageId);
      if (mockMsg) {
        setContextMessages([
          { ...mockMsg, id: mockMsg.id - 2, messageText: 'Hey, how are you?', aiFlagged: false, severity: 'LOW' },
          { ...mockMsg, id: mockMsg.id - 1, messageText: 'Good to see you online!', aiFlagged: false, severity: 'LOW' },
          mockMsg,
          { ...mockMsg, id: mockMsg.id + 1, messageText: 'Whoa, that was harsh...', aiFlagged: false, severity: 'LOW' },
          { ...mockMsg, id: mockMsg.id + 2, messageText: 'Yeah, not cool.', aiFlagged: false, severity: 'LOW' },
        ]);
      }
    } finally {
      setIsLoadingContext(false);
    }
  }, [messages]);

  // Handle message selection
  const handleSelectMessage = (message: FlaggedMessage) => {
    setSelectedMessage(message);
    loadContext(message.id);
  };

  // Handle action
  const handleAction = async (action: string) => {
    if (!selectedMessage) return;

    setIsActioning(true);
    try {
      await fetchApi(`/api/v1/admin/chat/flagged/${selectedMessage.id}/review`, {
        method: 'PUT',
        body: JSON.stringify({ action }),
      });

      // Reload data
      await loadMessages();
      await loadStats();
      setSelectedMessage(null);
      setContextMessages([]);
    } catch (err) {
      console.error('Failed to review message:', err);
      setError(err instanceof Error ? err.message : 'Failed to review message');
    } finally {
      setIsActioning(false);
    }
  };

  // Load data on mount and filter change
  useEffect(() => {
    loadMessages();
    loadStats();
  }, [loadMessages, loadStats]);

  // Mock data for development
  function getMockMessages(): FlaggedMessage[] {
    return [
      {
        id: 1,
        squadId: 5,
        squadName: 'Tennis Club NYC',
        senderId: 100,
        senderName: 'John Smith',
        senderEmail: 'john@example.com',
        messageText: 'This is such a stupid rule, I hate this garbage policy!',
        messageType: 'TEXT',
        aiFlagged: true,
        aiToxicityScore: 0.85,
        aiFlagReason: 'NEGATIVE_LANGUAGE',
        severity: 'HIGH',
        reviewed: false,
        createdAt: new Date(Date.now() - 3600000).toISOString(),
      },
      {
        id: 2,
        squadId: 12,
        squadName: 'Weekend Warriors',
        senderId: 101,
        senderName: 'Jane Doe',
        senderEmail: 'jane@example.com',
        messageText: 'You f***ing loser, get out of the squad!',
        messageType: 'TEXT',
        aiFlagged: true,
        aiToxicityScore: 1.0,
        aiFlagReason: 'SLUR',
        severity: 'CRITICAL',
        reviewed: false,
        createdAt: new Date(Date.now() - 7200000).toISOString(),
      },
      {
        id: 3,
        squadId: 8,
        squadName: 'Morning Tennis',
        senderId: 102,
        senderName: 'Mike Johnson',
        senderEmail: 'mike@example.com',
        messageText: 'Check out this amazing deal! Click here for free money: bit.ly/scam123',
        messageType: 'TEXT',
        aiFlagged: true,
        aiToxicityScore: 0.9,
        aiFlagReason: 'SPAM_URL',
        severity: 'CRITICAL',
        reviewed: false,
        createdAt: new Date(Date.now() - 86400000).toISOString(),
      },
      {
        id: 4,
        squadId: 5,
        squadName: 'Tennis Club NYC',
        senderId: 103,
        senderName: 'Sarah Wilson',
        senderEmail: 'sarah@example.com',
        messageText: 'That was a terrible shot, absolutely pathetic performance today.',
        messageType: 'TEXT',
        aiFlagged: true,
        aiToxicityScore: 0.55,
        aiFlagReason: 'NEGATIVE_LANGUAGE',
        severity: 'MEDIUM',
        reviewed: false,
        createdAt: new Date(Date.now() - 172800000).toISOString(),
      },
      {
        id: 5,
        squadId: 15,
        squadName: 'Casual Players',
        senderId: 104,
        senderName: 'Alex Brown',
        senderEmail: 'alex@example.com',
        messageText: 'The weather today is trash, not great for tennis.',
        messageType: 'TEXT',
        aiFlagged: true,
        aiToxicityScore: 0.35,
        aiFlagReason: 'NEGATIVE_LANGUAGE',
        severity: 'LOW',
        reviewed: false,
        createdAt: new Date(Date.now() - 259200000).toISOString(),
      },
    ];
  }

  // Severity filter buttons config
  const severityFilters: Array<{ key: Severity; label: string; count?: number; color?: string }> = [
    { key: 'ALL', label: 'All', count: stats?.total },
    { key: 'CRITICAL', label: 'Critical', count: stats?.critical, color: 'text-red-400' },
    { key: 'HIGH', label: 'High', count: stats?.high, color: 'text-orange-400' },
    { key: 'MEDIUM', label: 'Medium', count: stats?.medium, color: 'text-amber-400' },
    { key: 'LOW', label: 'Low', count: stats?.low, color: 'text-slate-400' },
  ];

  return (
    <div className="flex flex-col min-h-0 h-full">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between mb-4">
        <div>
          <h1 className="text-2xl font-bold text-white flex items-center gap-3">
            <MessageSquare className="h-7 w-7 text-cyan-400" />
            Flagged Messages
          </h1>
          <p className="mt-1 text-sm text-slate-400">
            AI-detected inappropriate content
          </p>
        </div>

        <button
          onClick={() => { loadMessages(); loadStats(); }}
          disabled={isLoading}
          className="inline-flex items-center gap-2 px-3 py-2 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-300 rounded-lg transition-colors"
        >
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <RefreshCw className="h-4 w-4" />
          )}
          Refresh
        </button>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-5 gap-4 mb-4">
          <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
            <div className="text-2xl font-bold text-red-400">{stats.critical}</div>
            <div className="text-sm text-slate-400">Critical</div>
          </div>
          <div className="p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg">
            <div className="text-2xl font-bold text-orange-400">{stats.high}</div>
            <div className="text-sm text-slate-400">High</div>
          </div>
          <div className="p-4 bg-amber-500/10 border border-amber-500/20 rounded-lg">
            <div className="text-2xl font-bold text-amber-400">{stats.medium}</div>
            <div className="text-sm text-slate-400">Medium</div>
          </div>
          <div className="p-4 bg-slate-500/10 border border-slate-500/20 rounded-lg">
            <div className="text-2xl font-bold text-slate-400">{stats.low}</div>
            <div className="text-sm text-slate-400">Low</div>
          </div>
          <div className="p-4 bg-cyan-500/10 border border-cyan-500/20 rounded-lg">
            <div className="text-2xl font-bold text-cyan-400">{stats.total}</div>
            <div className="text-sm text-slate-400">Total</div>
          </div>
        </div>
      )}

      {/* Filters */}
      <div className="flex items-center gap-4 mb-4">
        <div className="flex items-center gap-1 p-1 bg-slate-800 rounded-lg">
          {severityFilters.map(({ key, label, count, color }) => (
            <button
              key={key}
              onClick={() => { setSeverityFilter(key); setPage(0); }}
              className={`px-3 py-1.5 rounded-md text-sm transition-colors ${
                severityFilter === key
                  ? 'bg-cyan-600 text-white'
                  : `${color || 'text-slate-400'} hover:text-white hover:bg-slate-700`
              }`}
            >
              {label}
              {count !== undefined && count > 0 && (
                <span className={`ml-1 px-1.5 py-0.5 rounded text-xs ${
                  severityFilter === key ? 'bg-cyan-500' : 'bg-slate-700'
                }`}>
                  {count}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* Main content */}
      <div className="flex-1 flex gap-4 min-h-0">
        {/* Messages list */}
        <div className="flex-1 overflow-y-auto">
          {error && (
            <div className="flex items-center gap-3 p-4 mb-4 bg-red-900/20 border border-red-800/50 rounded-lg text-red-400">
              <AlertCircle className="h-5 w-5" />
              {error}
            </div>
          )}

          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-cyan-400" />
            </div>
          ) : messages.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-slate-500">
              <CheckCircle2 className="h-12 w-12 mb-3 opacity-50 text-green-400" />
              <p className="text-green-400">No flagged messages</p>
              <p className="text-sm mt-1">All messages are clean!</p>
            </div>
          ) : (
            <div className="grid gap-3">
              {messages.map((message) => (
                <FlaggedMessageCard
                  key={message.id}
                  message={message}
                  onClick={() => handleSelectMessage(message)}
                  isSelected={selectedMessage?.id === message.id}
                />
              ))}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-800">
              <div className="text-sm text-slate-500">
                Showing {messages.length} of {totalItems} messages
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-300 text-sm rounded-lg transition-colors"
                >
                  Previous
                </button>
                <span className="text-sm text-slate-400">
                  Page {page + 1} of {totalPages}
                </span>
                <button
                  onClick={() => setPage((p) => Math.min(totalPages - 1, p + 1))}
                  disabled={page >= totalPages - 1}
                  className="px-3 py-1.5 bg-slate-800 hover:bg-slate-700 disabled:opacity-50 text-slate-300 text-sm rounded-lg transition-colors"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Context panel */}
        {selectedMessage && (
          <div className="w-[450px] flex-shrink-0 flex flex-col bg-slate-900 border border-slate-800 rounded-lg">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b border-slate-800">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-400" />
                <span className="font-medium text-white">Message Context</span>
              </div>
              <button
                onClick={() => { setSelectedMessage(null); setContextMessages([]); }}
                className="p-1 rounded hover:bg-slate-800 text-slate-400 hover:text-white"
              >
                <X className="h-5 w-5" />
              </button>
            </div>

            {/* Context */}
            <div className="flex-1 overflow-y-auto p-4">
              {isLoadingContext ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-cyan-400" />
                </div>
              ) : (
                <MessageContextViewer
                  messages={contextMessages}
                  flaggedMessageId={selectedMessage.id}
                />
              )}
            </div>

            {/* Actions */}
            <div className="p-4 border-t border-slate-800">
              <div className="text-xs text-slate-500 mb-3">Take Action</div>
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => handleAction('APPROVE')}
                  disabled={isActioning}
                  className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-500 disabled:opacity-50 text-white text-sm rounded-lg transition-colors"
                >
                  <CheckCircle2 className="h-4 w-4" />
                  Approve
                </button>
                <button
                  onClick={() => handleAction('DELETE')}
                  disabled={isActioning}
                  className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-slate-700 hover:bg-slate-600 disabled:opacity-50 text-white text-sm rounded-lg transition-colors"
                >
                  <Trash2 className="h-4 w-4" />
                  Delete
                </button>
                <button
                  onClick={() => handleAction('WARN_USER')}
                  disabled={isActioning}
                  className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-amber-600 hover:bg-amber-500 disabled:opacity-50 text-white text-sm rounded-lg transition-colors"
                >
                  <AlertOctagon className="h-4 w-4" />
                  Warn User
                </button>
                <button
                  onClick={() => handleAction('BAN_USER')}
                  disabled={isActioning}
                  className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white text-sm rounded-lg transition-colors"
                >
                  <Ban className="h-4 w-4" />
                  Ban User
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
